import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { Link as RouterLink } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import notFoundBg from '@/assets/not-found/404-bg.jpg'
import blueFlame from '@/assets/not-found/blue-flame.png'

interface NotFoundPageProps {
  /** Destination for “Back to home”. Defaults to `/`. */
  homeTo?: string
}

const HEADING_COLOR = '#00adee'
const DIVIDER_COLOR = 'rgba(96, 165, 250, 0.5)'
const FOOTER_COLOR = '#c7d2e9'
const BUTTON_BG = '#008cd0'
const DIGIT_SHADOW = '0px 0px 40px rgba(59, 130, 246, 0.4), 0px 0px 20px #00adee'

export function NotFoundPage({ homeTo = '/' }: Readonly<NotFoundPageProps>) {
  useDocumentTitle('Page not found')

  return (
    <Box
      component="main"
      data-testid="not-found-page"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        px: { xs: 2, sm: 3 },
        py: { xs: 4, sm: 6 },
        boxSizing: 'border-box',
        backgroundColor: '#020617',
        backgroundImage: `url(${notFoundBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: 560,
          gap: { xs: 3, sm: 4 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <Box
            aria-hidden
            sx={{ width: 32, height: '1px', bgcolor: DIVIDER_COLOR, flexShrink: 0 }}
          />
          <Typography
            component="h1"
            sx={{
              color: HEADING_COLOR,
              fontWeight: 700,
              fontSize: { xs: '18px', sm: '24px', md: '33px' },
              lineHeight: { xs: '22px', sm: '28px', md: '20px' },
              letterSpacing: '2.8px',
              textTransform: 'uppercase',
              textAlign: 'center',
              whiteSpace: { xs: 'normal', md: 'nowrap' },
            }}
          >
            Oops! Page not found
          </Typography>
          <Box
            aria-hidden
            sx={{ width: 32, height: '1px', bgcolor: DIVIDER_COLOR, flexShrink: 0 }}
          />
        </Box>

        <Typography
          sx={{
            color: '#ffffff',
            fontWeight: 400,
            fontSize: { xs: '16px', sm: '18px' },
            lineHeight: '29.25px',
            textAlign: 'center',
            maxWidth: 504,
          }}
        >
          The page you&apos;re looking for doesn&apos;t exist, has been moved, or the link may be
          incorrect.
        </Typography>

        <Box
          aria-hidden
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: '8px', sm: '12px', md: '17px' },
            my: { xs: 1, sm: 2 },
          }}
        >
          <Typography
            component="span"
            sx={{
              color: '#ffffff',
              fontWeight: 900,
              fontSize: { xs: '96px', sm: '180px', md: '320px' },
              lineHeight: 1,
              textShadow: DIGIT_SHADOW,
              userSelect: 'none',
            }}
          >
            4
          </Typography>
          <Box
            sx={{
              width: { xs: 72, sm: 140, md: 224 },
              height: { xs: 116, sm: 226, md: 363 },
              filter: 'drop-shadow(0px 0px 30px rgba(59, 130, 246, 0.6))',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={blueFlame}
              alt=""
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
          <Typography
            component="span"
            sx={{
              color: '#ffffff',
              fontWeight: 900,
              fontSize: { xs: '96px', sm: '180px', md: '320px' },
              lineHeight: 1,
              textShadow: DIGIT_SHADOW,
              userSelect: 'none',
            }}
          >
            4
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            width: '100%',
            maxWidth: 462,
          }}
        >
          <Button
            component={RouterLink}
            to={homeTo}
            variant="contained"
            fullWidth
            data-testid="not-found-back-home"
            aria-label="Back to home"
            sx={{
              bgcolor: BUTTON_BG,
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '24px',
              letterSpacing: '0.15px',
              textTransform: 'uppercase',
              borderRadius: '8px',
              px: 3,
              py: 2,
              minHeight: 56,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#0077b3',
                boxShadow: 'none',
              },
            }}
          >
            Back to home
          </Button>
          <Typography
            sx={{
              color: FOOTER_COLOR,
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.35px',
              textAlign: 'center',
            }}
          >
            Let&apos;s get you back on track.
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
